import React, { Fragment, PropsWithChildren, useEffect, useState } from 'react';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import type { Ticket } from './Ticket';
import {
  Accordion,
  AccordionElement,
  AccordionHeader,
  AccordionHeading,
  AccordionIcon,
  AccordionPanel,
  AccordionTitle,
  AccordionToggleButton,
  useAccordion,
} from '../../../_lib/components/Accordion';
import { Tickets } from './Tickets';
import { Heading } from '../../../_lib/components/Heading/Heading';
import { PersonInformation } from '../../../components/molecules/PersonInformation';
import { ContactPerson } from '../../../_lib/components/ContactPerson';

export type TicketsWithSupplier = {
  date: string;
  ticketsOfSupplier: [
    {
      supplier?: ContactPerson;
      firstToBeNotified?: ContactPerson;
      secondToBeNotified?: ContactPerson;
      tickets: Ticket[];
    },
  ];
};

export type RawTickets = {
  date: string;
  tickets: Ticket[];
};

export type TicketsByDate = RawTickets | TicketsWithSupplier;

export type TicketAccordionDefaultProps = {
  ticketsOfDate: TicketsByDate[];
};

function isRawTickets(variant: RawTickets | TicketsWithSupplier): variant is RawTickets {
  return 'tickets' in variant;
}

function normalizeTicketData(ticketsOfDate: TicketsByDate[], language: string) {
  if (!ticketsOfDate) {
    return [];
  }

  return ticketsOfDate.map((items) => {
    const date = new Date(items.date);
    return {
      category: date.toLocaleDateString(language, {
        weekday: 'long',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      }),
      id: `${items.date}`,
      props: items,
    };
  });
}

function ticketsStatistics(tickets: Ticket[]) {
  return {
    numberOpenTickets: tickets.filter(ticket => ticket.ticketState === 'open').length,
    numberTickets: tickets.length,
  };
}

export function TicketAccordion({ ticketsOfDate }: PropsWithChildren<TicketAccordionDefaultProps>) {
  const { i18n, t } = useSonTranslation('TicketBooking');
  const [data, setData] = useState(normalizeTicketData(ticketsOfDate, i18n.language));
  const { getAccordionProps, items } = useAccordion(data, {
    initialActiveElementId: undefined,
  });

  useEffect(() => {
    setData(normalizeTicketData(ticketsOfDate, i18n.language));
  }, [i18n.language, ticketsOfDate]);

  if (!data.length) {
    return null;
  }

  return (
    <Accordion {...getAccordionProps()}>
      {items.map(({ getElementProps, getToggleButtonProps, getPanelProps, category, id, props }) => (
        <AccordionElement {...getElementProps()} key={`AccordionElement-${id}`}>
          <AccordionHeader>
            <AccordionHeading hierarchy={3}>
              <AccordionToggleButton {...getToggleButtonProps()}>
                <AccordionTitle modifier="width-200">
                  <time dateTime={props.date}>{category}</time>
                </AccordionTitle>
                <span className="ticket-booking__ticket-overview">
                  {isRawTickets(props)
                    ? t('default.ticketsOverview', ticketsStatistics(props.tickets))
                    : t(
                        'default.ticketsOverview',
                        ticketsStatistics(props.ticketsOfSupplier.flatMap(el => el.tickets)),
                      )}
                </span>
                <AccordionIcon />
              </AccordionToggleButton>
            </AccordionHeading>
          </AccordionHeader>
          <AccordionPanel {...getPanelProps()}>
            {isRawTickets(props)
              ? (
                  <Tickets tickets={props.tickets} />
                )
              : (
                  props.ticketsOfSupplier?.map(item => (
                    <Fragment key={`${id}-${item.tickets[0].ticketId}`}>
                      <div className="ticket-booking__supplier">
                        {item.supplier
                          ? (
                              <div className="ticket-booking__supplier-container">
                                <Heading className="ticket-booking__supplier-label" hierarchy={4}>
                                  {t('default.supplierLabel')}
                                </Heading>
                                <PersonInformation {...item.supplier} />
                              </div>
                            )
                          : (
                              <p className="ticket-booking__no-supplier">{t('default.noSupplier')}</p>
                            )}
                        {(item.firstToBeNotified || item.secondToBeNotified) && (
                          <div className="ticket-booking__persons">
                            <Heading className="ticket-booking__supplier-label" hierarchy={4}>
                              {t('default.personsToBeNotifiedLabel')}
                            </Heading>
                            <div className="ticket-booking__persons-container">
                              {item.firstToBeNotified && <PersonInformation {...item.firstToBeNotified} />}
                              {item.secondToBeNotified && <PersonInformation {...item.secondToBeNotified} />}
                            </div>
                          </div>
                        )}
                      </div>
                      <Tickets tickets={item.tickets} />
                    </Fragment>
                  ))
                )}
          </AccordionPanel>
        </AccordionElement>
      ))}
    </Accordion>
  );
}
